// #32 — the dock's wall playback card. Figma: Shared / Dock / Wall playback card 4265:2156; in the dock: Web / Dock /
// Wall playback / Over another screen 4267:5297 (dark 4267:18890) and Stacked / Cast + wall card 4267:5400 (dark
// 4267:18900). Minimised (owner 2026-10-21): Android / Dock / Mini player 4304:10630, Web / Dock / Mini player 4308:11316.
// Fullscreen + YouTube's own controls (owner 2026-10-21): Media=YouTube Expanded 4313:12468, Video 4313:12466, Web / Wall
// playback / Fullscreen · YouTube 4314:12567 (dark 4314:12590), band 4317:12486; header full screen 4317:12474 / 4317:12475.
// Full screen = the shared controls, embed band under the player (Shared / Player / Controls Web Embed 4351:2484 /
// 4351:2664): auto-hide after 4 s, a tap on the black area shows them.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, CircleStop, ListMusic, LocateFixed, Maximize, Minimize, Pause, Play, SkipBack, SkipForward, Square } from "lucide-react";
import type { WallItem, WallPost } from "../../api/content";
import { formatDuration } from "../wall/formatDuration";
import { uploadTitle } from "../wall/uploadRow";
import { isLinkItem, isWallItemHeld, nextPlayable, useWallPlaybackStore } from "../../store/wallPlaybackStore";
import WallDockPlayer from "./WallDockPlayer";
import { useElementFullscreen } from "./wallFullscreen";
import FullScreenPlayerControls from "./controls/FullScreenPlayerControls";
import type { PlayerControlsSpec } from "./controls/playerControlsSpec";
import { usePlayerControlsVisibility } from "./controls/usePlayerControlsVisibility";
import MiniPlayerInfo, { type DockMini } from "./MiniPlayerInfo";
import { useAppOverlayStore } from "../../store/appOverlayStore";
import { useDockMiniStore } from "../../store/dockMiniStore";
import { WALL_KEY } from "../../lib/dockMini";
import { kindIcon, kindOf } from "../../player";
import { shownImage, useExternalMediaAllowed } from "../../lib/externalMedia";

function Art({ item, size }: { item: WallItem; size: 48 | 64 }) {
  const cls = size === 64 ? "size-16 rounded-xl" : "size-12 rounded-lg";
  const externalOk = useExternalMediaAllowed();
  const thumb = shownImage(item.thumbnailUrl, externalOk);
  if (thumb) return <img src={thumb} alt="" className={`${cls} object-cover shrink-0`} />;
  const Glyph = kindIcon(kindOf(item));
  return (
    <div className={`${cls} shrink-0 bg-[var(--bg-elevated)] text-[var(--foreground-muted)] flex items-center justify-center`}>
      <Glyph size={24} aria-hidden="true" />
    </div>
  );
}

const iconBtn = "shrink-0 flex items-center justify-center text-[var(--foreground)] disabled:opacity-40";

interface Props {
  /** #3: the header's Go to post (StreamDock's useGoToWallPost); playback is left as it is. */
  onGoToPost?(post: WallPost): void;
  /** The dock is the mini player and this card is the one it shows. */
  mini?: DockMini | null;
  /** The dock is the mini player and another player shows: not displayed (a YouTube / SoundCloud item holds). */
  hidden?: boolean;
}

export default function WallDockCard({ onGoToPost, mini = null, hidden = false }: Props = {}) {
  const { t } = useTranslation();
  const current = useWallPlaybackStore((s) => s.current);
  const store = useWallPlaybackStore.getState;
  const item = current ? current.post.items[current.index] : null;
  const itemKey = current ? `${current.post.id}|${item?.id}` : null;
  // Keyed by item: a report of the previous item never shows on the next one's first frame.
  // An app overlay (MediaViewer, a composer / uploads / cast modal) covers the dock (Android: window unfocused).
  const covered = useAppOverlayStore((s) => s.count > 0);
  // The video box is not wholly in view (the capped dock scrolled it).
  const [clipped, setClipped] = useState(false);
  // The YouTube player shows its own controls (keyed by item, like `reported`).
  const [ownControlsKey, setOwnControlsKey] = useState<string | null>(null);
  const [reported, setReported] = useState<{ key: string | null; pos: number; dur: number | null }>({ key: null, pos: 0, dur: null });

  // Fullscreen is this card's wrapper around the player, never the per-item player: Prev / Next / the next item
  // after one ends stay fullscreen (the player inside is keyed by item and remounts).
  const [fsEl, setFsEl] = useState<HTMLDivElement | null>(null);
  const fs = useElementFullscreen(fsEl);
  const fsLink = item != null && isLinkItem(item);
  // No video area to show (an audio / radio item came next): back to the card.
  useEffect(() => {
    if (fs.on && !fsLink) fs.exit();
  }, [fs, fsLink]);
  // The shared controls: shown on entering and on each new item, hidden 4 s later; up while paused.
  const vis = usePlayerControlsVisibility({ active: fs.on, itemKey: itemKey ?? undefined });
  const fsPaused = fs.on && !!current?.paused;
  const { hold } = vis;
  useEffect(() => hold("paused", fsPaused), [hold, fsPaused]);
  // Full screen: the link player's Tap to listen (autoplay blocked), drawn in the band (a control: hides with it).
  const [tapToListen, setTapToListen] = useState<{ tap(): void } | null>(null);

  if (!current || !item) return null;
  const { post, wallName, index, paused, failed } = current;
  const total = post.items.length;
  const n = index + 1;
  const title = uploadTitle(post) || t("my_uploads_untitled", "Untitled");
  const itemTitle = item.title ?? item.url ?? "";
  const subline = wallName
    ? t("dock_wall_label", "Wall · {{wall}} · {{n}} / {{total}}", { wall: wallName, n, total })
    : t("dock_wall_label_nowall", "Wall · {{n}} / {{total}}", { n, total });
  const nextIdx = nextPlayable(post.items, index, 1);
  const prevIdx = nextPlayable(post.items, index, -1);
  const nextItem = nextIdx != null ? post.items[nextIdx] : null;
  const kind = kindOf(item);
  const live = kind.caps.live;
  const provider = kind.meta(t);
  const progress = reported.key === itemKey ? reported : { pos: 0, dur: item.durationMs };
  const dur = progress.dur ?? item.durationMs;
  const link = isLinkItem(item);
  // Held: not the user's pause (`paused` is kept, so it resumes). The mini player is never a reason (owner 2026-10-21).
  const held = isWallItemHeld(item, { hidden, covered, clipped });
  const miniVideo = mini != null && link;
  const playerPaused = paused || held;
  // Owner 2026-10-21: YouTube's own controls already play / pause / seek and show the times, so the full card drops
  // ours (prev / next, Go to post, collapse, stop stay). Wall playback only: cast cards keep theirs.
  const ytOwn = kind.caps.ownControls && ownControlsKey === itemKey;
  const pct = dur && dur > 0 ? Math.min(100, Math.max(0, (progress.pos / dur) * 100)) : 0;

  const stop = (
    <button type="button" data-testid="wall-dock-stop" className={iconBtn} aria-label={t("dock_stop", "Stop")} title={t("dock_stop", "Stop")} onClick={() => store().stop()}>
      <CircleStop size={18} aria-hidden="true" />
    </button>
  );

  // Full screen (band under the 16:9 player, never over it: YouTube's policy): exit · title · stop, then prev · next —
  // play / pause too when YouTube's own controls are not there (SoundCloud's widget is shielded).
  const fsSpec: PlayerControlsSpec = {
    embed: "bandUnder",
    title: itemTitle || title,
    meta: t("dock_wall_item_of", "{{provider}} · item {{n}} of {{total}}", { provider, n, total }),
    top: [
      { testId: "wall-dock-exit-fullscreen", label: t("exit_fullscreen"), icon: <Minimize aria-hidden="true" />, onClick: fs.exit, start: true },
      { testId: "wall-dock-fs-stop", label: t("dock_stop", "Stop"), icon: <Square aria-hidden="true" />, onClick: () => store().stop(), variant: "destructive" },
    ],
    middle: [
      { testId: "wall-dock-fs-prev", label: t("dock_previous", "Previous"), icon: <SkipBack aria-hidden="true" />, onClick: () => store().prev(), disabled: prevIdx == null },
      ...(ytOwn
        ? []
        : [{
            testId: "wall-dock-fs-playpause",
            label: paused ? t("dock_play", "Play") : t("dock_pause", "Pause"),
            icon: paused ? <Play aria-hidden="true" /> : <Pause aria-hidden="true" />,
            onClick: () => store().togglePause(),
          }]),
      { testId: "wall-dock-fs-next", label: t("dock_next", "Next"), icon: <SkipForward aria-hidden="true" />, onClick: () => store().next(), disabled: nextIdx == null },
    ],
    bottomExtra: fs.on && tapToListen ? (
      <button
        type="button"
        data-testid="btn-tap-to-listen"
        onClick={tapToListen.tap}
        className="h-8 rounded-full bg-white/16 px-4 text-xs font-medium text-white focus-visible:outline-2 focus-visible:outline-white"
      >
        ▶ {t("tap_to_listen", "Tap to listen")}
      </button>
    ) : undefined,
  };

  return (
    <div
      data-testid="wall-dock-card"
      data-mini={mini ? (miniVideo ? "video" : "audio") : undefined}
      data-held={held ? "true" : undefined}
      className={`relative flex shrink-0 overflow-hidden rounded-xl bg-[var(--bg-base)] shadow-lg ${
        mini ? `flex-row gap-3 p-2 ${miniVideo ? "items-stretch" : "items-center"}` : "flex-col pb-2"
      }`}
      style={hidden ? { display: "none" } : undefined}
    >
      {/* The player stays the second child in every form: floating ↔ mini never remounts it (playback goes on). */}
      {mini ? (
        !miniVideo && <Art item={item} size={48} />
      ) : (
        <div className="flex items-center gap-2 p-2">
          <div className="size-8 shrink-0 rounded-full bg-[var(--bg-elevated)] text-[var(--foreground)] flex items-center justify-center">
            <ListMusic size={18} aria-hidden="true" />
          </div>
          <div className="flex flex-1 min-w-0 flex-col">
            <span className="truncate text-sm font-bold leading-none text-[var(--foreground)]">{title}</span>
            <span className="truncate text-xs leading-[1.3] text-[var(--foreground-muted)]">{subline}</span>
          </div>
          {/* Figma 4265:2156 (#3, 4299:9743-9746): [Go to post] [collapse] [stop]. Collapse minimises the whole dock. */}
          <div className="flex items-center gap-2 shrink-0">
            {onGoToPost && (
              <button
                type="button"
                data-testid="dock-go-to-post"
                className={iconBtn}
                aria-label={t("dock_go_to_post", "Go to post")}
                title={t("dock_go_to_post", "Go to post")}
                onClick={() => onGoToPost(post)}
              >
                <LocateFixed size={18} aria-hidden="true" />
              </button>
            )}
            {/* 4317:12475: go to post · full screen · minimise · stop (never on the player: YouTube's policy). */}
            {fsLink && fs.supported && (
              <button
                type="button"
                data-testid="wall-dock-fullscreen"
                className={iconBtn}
                aria-label={t("fullscreen")}
                title={t("fullscreen")}
                onClick={fs.toggle}
              >
                <Maximize size={20} aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              data-testid="wall-dock-collapse"
              className={iconBtn}
              aria-label={t("dock_collapse", "Collapse")}
              title={t("dock_collapse", "Collapse")}
              onClick={() => useDockMiniStore.getState().minimise(WALL_KEY)}
            >
              <ChevronDown size={20} aria-hidden="true" />
            </button>
            {stop}
          </div>
        </div>
      )}

      {/* Fullscreen = this wrapper (player + shared controls), never the per-item player: Prev / Next stay fullscreen.
          The shared controls keep one tree in both forms, so the player is never remounted. */}
      <div
        ref={setFsEl}
        data-testid="wall-dock-fs-wrap"
        data-fullscreen={fs.on ? "true" : undefined}
        className={fs.on ? "bg-black" : "shrink-0"}
      >
        <FullScreenPlayerControls spec={fsSpec} visibility={vis} on={fs.on}>
          <WallDockPlayer
            item={item}
            paused={playerPaused}
            held={held}
            mini={mini != null}
            fullscreen={fs.on}
            onTapToListen={(tap) => setTapToListen(tap ? { tap } : null)}
            onClipped={setClipped}
            onOwnControls={(on) => setOwnControlsKey(on ? itemKey : null)}
            // Paused / played inside YouTube = the user's pause / play. A play pressed during a hold never gets here:
            // the player pauses it again itself (the hold wins).
            onUserPaused={(p) => {
              if (store().current?.paused !== p) store().togglePause();
            }}
            onEnded={() => store().ended()}
            onError={() => store().failed()}
            onProgress={(pos, d) => setReported((r) => ({ key: itemKey, pos, dur: d ?? (r.key === itemKey ? r.dur : null) }))}
          />
        </FullScreenPlayerControls>
      </div>

      {mini ? (
        <MiniPlayerInfo
          mini={mini}
          title={itemTitle}
          line={subline}
          video={miniVideo}
          // Held is not playing either: the button offers Play (a tap resumes as the user's play).
          playing={!(paused || held)}
          onTogglePlay={() => store().togglePause()}
          onStop={() => store().stop()}
          actions={
            onGoToPost && (
              <button
                type="button"
                data-testid="dock-go-to-post"
                className="size-10 shrink-0 rounded-full flex items-center justify-center text-[var(--foreground)] hover:bg-[var(--bg-hover)]"
                aria-label={t("dock_go_to_post", "Go to post")}
                title={t("dock_go_to_post", "Go to post")}
                onClick={() => onGoToPost(post)}
              >
                <LocateFixed size={18} aria-hidden="true" />
              </button>
            )
          }
        />
      ) : (
        <div className="flex flex-col gap-2 px-2">
          <div className="flex items-center gap-3">
            <Art item={item} size={64} />
            <div className="flex flex-1 min-w-0 flex-col gap-0.5">
              <span className="truncate text-sm font-bold leading-none text-[var(--foreground)]">{itemTitle}</span>
              <span className="truncate text-xs leading-[1.3] text-[var(--foreground-muted)]">
                {t("dock_wall_item_of", "{{provider}} · item {{n}} of {{total}}", { provider, n, total })}
              </span>
              {failed ? (
                <span role="status" data-testid="wall-dock-failed" className="truncate text-xs leading-[1.3] text-[var(--destructive)]">
                  {t("cant_play_link", "Can't play this link")}
                </span>
              ) : (
                nextItem && (
                  <span className="truncate text-xs leading-[1.3] text-[var(--foreground-muted)]">
                    {t("dock_wall_next", "Next · {{title}}", { title: nextItem.title ?? nextItem.url ?? "" })}
                  </span>
                )
              )}
            </div>
          </div>

          {ytOwn ? null : live ? (
            <span className="text-xs leading-[1.3] text-[#4cb0a6]">{provider}</span>
          ) : (
            <div className="flex flex-col gap-1">
              <div className="h-1 w-full shrink-0 overflow-hidden rounded-full bg-[var(--bg-elevated)]">
                <div data-testid="wall-dock-played" className="h-1 rounded-sm bg-[var(--primary)]" style={{ width: `${pct}%` }} />
              </div>
              <div className="flex justify-between text-xs leading-[1.3] text-[var(--foreground-muted)]">
                <span>{formatDuration(progress.pos) ?? "0:00"}</span>
                <span>{formatDuration(dur) ?? ""}</span>
              </div>
            </div>
          )}

          <div className="flex items-center justify-center gap-4">
            <button type="button" className={iconBtn} aria-label={t("dock_previous", "Previous")} title={t("dock_previous", "Previous")} disabled={prevIdx == null} onClick={() => store().prev()}>
              <SkipBack size={24} aria-hidden="true" />
            </button>
            {!ytOwn && (
              <button
                type="button"
                data-testid="wall-dock-playpause"
                className="size-10 shrink-0 rounded-full bg-[var(--primary)] text-[var(--primary-foreground)] flex items-center justify-center"
                aria-label={paused ? t("dock_play", "Play") : t("dock_pause", "Pause")}
                title={paused ? t("dock_play", "Play") : t("dock_pause", "Pause")}
                onClick={() => store().togglePause()}
              >
                {paused ? <Play size={24} aria-hidden="true" /> : <Pause size={24} aria-hidden="true" />}
              </button>
            )}
            {/* Stays usable on a failed item: Next is how the listener moves on. */}
            <button type="button" className={iconBtn} aria-label={t("dock_next", "Next")} title={t("dock_next", "Next")} disabled={nextIdx == null} onClick={() => store().next()}>
              <SkipForward size={24} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
